import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { EmptyObject } from 'lib/object/objectUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import LuxLPPPricingLabeledSheet from 'luxPlus/components/LuxLPPPricingLabeledSheet'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import React, { useMemo } from 'react'
import SearchTileStackAmenitiesList from '../shared/SearchTileStackAmenitiesList'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import { isStandardOccupancy } from 'lib/search/searchUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from '../../../../hooks/Optimizely/useOptimizelyExperiment'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  truncateAfter?: number
  singleColumn?: boolean
}

function BedbankSearchTileFeatures({ offer, filters = EmptyObject, truncateAfter, singleColumn }: Props) {
  const amenities = useMemo<Array<App.OfferInclusion>>(() => {
    return offer.popularFacilities.map(facility => ({
      description: facility,
      id: facility,
      symbol: determineOfferFeatureSymbol(facility),
    }))
  }, [offer.popularFacilities])

  const { offerMemberPrice, offerPrice } = useBedbankOfferSearchPricing(offer, filters)

  const luxPlusLPPEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const directSearchPrices = useDirectSearchPrices({ filters, offerId: offer.id })
  const hasStandardOccupancy = isStandardOccupancy(filters)
  const showRoomBreakdown = !!useOptimizelyExperiment(OptimizelyExperiments.searchShowRoomBreakdown, !hasStandardOccupancy)

  return <Group direction="vertical" verticalAlign="space-between" gap={12} grow noDisplayWhenEmpty>
    <Group direction="vertical" gap={12} noDisplayWhenEmpty>
      {luxPlusLPPEnabled && <LuxLPPPricingLabeledSheet
        offer={offer}
        hasLuxPlusRate={directSearchPrices?.hasLuxPlusRate}
        offerMemberPrice={offerMemberPrice}
        offerPrice={offerPrice}
      />}
      {!!amenities.length && <SearchTileStackAmenitiesList amenities={amenities} truncateAfter={truncateAfter} singleColumn={singleColumn} />}
    </Group>
    {showRoomBreakdown && <SearchTileRoomBreakdown
      offer={offer}
      filters={filters}
    />}
  </Group>
}

export default BedbankSearchTileFeatures
